import { observable, action, computed, makeObservable, runInAction } from 'mobx'
import { selectionTypes, ADDITIONAL_INFO_DEFAULT_INPUT } from 'constants.js'
import { move } from '@dnd-kit/helpers'
import api from 'api.js'
import _ from 'lodash'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import Marker from './Marker'
import { dataStore, selectionStore } from './UIStore'
import { debounce } from 'lodash'
import { downloadKML } from 'utils.js'

const BOUNDS_BUFFER = 0.5

// A marker which has been added to a target group
class SavedMarker {
  @observable id
  @observable created
  @observable name = ''
  @observable description = ''
  @observable address = ''
  @observable color_id
  @observable hideName
  @observable visible
  @observable error
  @observable invalid = observable.map()
  @observable position = new Position()
  @observable editMarker = new Marker()
  // POI bucket flags
  @observable is_general = false
  @observable is_competitor = false
  @observable is_ally = false
  @observable icon_url = null
  @observable types = null
  @observable place_id = null
  @observable is_owner = true
  @observable additional_info = []
  @observable notes = ''
  @observable contact_detail = { given_name: '', surname: '', email: '', address: '', country: '' }
  @observable status
  @observable tag_ids = []

  constructor ({
    id,
    created,
    latitude,
    longitude,
    name,
    description,
    address,
    color,
    is_general,
    is_competitor,
    is_ally,
    icon_url,
    types,
    place_id,
    is_owner = true,
    additional_info = [],
    notes = '',
    contact_detail = {},
    shape_status = null,
    tag_ids = []
  } = {}) {
    makeObservable(this)
    this.id = id
    this.is_owner = is_owner
    this.created = created
    this.name = name || ''
    this.description = description || ''
    this.address = address || ''
    this.visible = true
    this.position.latitude = _.isNil(latitude) ? null : latitude
    this.position.longitude = _.isNil(longitude) ? null : longitude
    this.color_id = color || 1
    // POI bucket flags from backend
    this.is_general = !!is_general
    this.is_competitor = !!is_competitor
    this.is_ally = !!is_ally
    this.additional_info = additional_info
    this.notes = notes
    this.contact_detail = { given_name: '', surname: '', email: '', phone: '', address: '', country: '', ...contact_detail }
    this.status = shape_status
    this.tag_ids = tag_ids
    // Track whether this marker had no bucket flags at load time.
    // We use this to migrate old markers into the General category and
    // to persist that change back to the backend once.
    this._wasUnbucketedAtLoad = !this.is_general && !this.is_competitor && !this.is_ally

    // UI behaviour: if this saved marker has no bucket flags at all,
    // treat it as a General marker by default so it appears dark blue.
    if (this._wasUnbucketedAtLoad) {
      this.is_general = true
    }
    this.icon_url = icon_url || null
    this.types = types || null
    this.place_id = place_id || null
  }

  getTypeLabel () {
    return 'Marker'
  }

  findAdditionalInfoIndex (id) {
    return this.additional_info.findIndex(item => Number(item.info_item) === Number(id))
  }

  @computed get getColor () {
    return _.find(dataStore.markerColors, item => {
      return item.id === this.color_id
    })
  }

  @computed get highlighted () {
    if (!selectionStore.item) {
      return false
    }

    const analysis = selectionStore.item.analysis
    if (_.includes([
      selectionTypes.TARGET_GROUP,
      selectionTypes.GEOPOLYGON,
      selectionTypes.BOUNDARY
    ], selectionStore.type) && analysis) {
      if (analysis.markersMode) {
        return _.includes(analysis.markersList, this)
      }
    }
    return false
  }

  startEdit () {
    this.visible = false
    this.editMarker.root = this
    this.editMarker.initial = false
    this.editMarker.id = this.id
    this.editMarker.name = this.name
    this.editMarker.description = this.description
    this.editMarker.position.latitude = this.position.latitude
    this.editMarker.position.longitude = this.position.longitude
    this.editMarker.color_id = this.color_id
  }

  @action openPopup (e) {
    this.hideName = true
    let popup = `<div><h3>${this.name}</h3></div>`
    this.description && (popup += `<p>${this.description}</p>`)
    e.target.bindPopup(popup)
    e.target.openPopup()
    e.target._map.on({ popupclose: () => { this.hideName = false } })
  }

  get selection () {
    this.error = null
    this.visible = true
    return {
      item: this,
      type: selectionTypes.SAVED_MARKER
    }
  }

  // Since a point has no area, zooming to its bounds would result
  // in the maximum zoom level being used, which is usually too zoomed-in
  // to be useful. So add some buffer around the marker when calculating bounds.
  bounds () {
    return [
      [this.position.latitude - BOUNDS_BUFFER, this.position.longitude - BOUNDS_BUFFER],
      [this.position.latitude + BOUNDS_BUFFER, this.position.longitude + BOUNDS_BUFFER]
    ]
  }

  @catchError async rename (name) {
    if (!name) return
    await api.patchMarkerName(this.id, name)
  }

  @catchError async updatePosition (latitude, longitude) {
    const lat = latitude !== undefined ? latitude : this.position.latitude
    const lng = longitude !== undefined ? longitude : this.position.longitude
    await api.patchMarker(this.id, {
      latitude: lat,
      longitude: lng
    })
  }

  @catchError async copy (targetGroupId) {
    await api.postCopyMarker(this.id, { target_group_id: targetGroupId })
  }

  @catchError async exportKml (kmlFormat) {
    const response = await api.getMarkerKML(this.id, !kmlFormat, { responseType: 'blob' })
    downloadKML(this.name, response.data, kmlFormat)
  }

  @catchError async deleteSavedMarker () {
    await api.deleteMarker(this.id)
  }

  getAdditionalInfoItemValue = (id) => {
    const item = this.additional_info.find(item => item.id === id)
    if (item) {
      return item.input
    }
    return null
  }

  @action deleteAdditionalInfoItem = (id) => {
    this.additional_info = this.additional_info.filter(item => item.id !== id)
    this.saveAdditionalInfo()
  }

  @action updateAdditionalInfoItem = (id, field, value) => {
    const index = this.findAdditionalInfoIndex(id)

    if (index === -1) {
      const definition = dataStore.userAdditionalInfoItems.find(item => item.id === id)
      if (!definition) return

      this.additional_info.push({
        id: definition.id,
        order: this.additional_info.length + 1,
        info_item: definition.id,
        label: definition.label,
        type: definition.type,
        input: {
          ...ADDITIONAL_INFO_DEFAULT_INPUT[definition.type],
          [field]: value
        }
      })
    } else {
      const item = this.additional_info[index]
      this.additional_info[index] = {
        ...item,
        input: {
          ...item.input,
          [field]: value
        }
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateAdditionalInfoItemType = (id, type, label) => {
    const index = this.additional_info.findIndex(item => item.id === id)
    if (index === -1) return

    const item = this.additional_info[index]
    if (item.type === type) return

    this.additional_info[index] = {
      ...item,
      type,
      label,
      input: {
        ...ADDITIONAL_INFO_DEFAULT_INPUT[type],
        title: item.input ? item.input.title : null
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateNotes = (value) => {
    this.notes = value
    this.debouncedSaveAdditionalInfo()
  }

  debouncedSaveAdditionalInfo = debounce((id) => {
    this.saveAdditionalInfo(id)
  }, 500)

  @action updateContactDetail = (field, value) => {
    this.contact_detail = { ...this.contact_detail, [field]: value }
    this.debouncedSaveContactDetail()
  }

  debouncedSaveContactDetail = debounce(() => {
    this.saveContactDetail()
  }, 800)

  @catchError async addAdditionalInfoItem (item) {
    runInAction(() => { this.additional_info.push(item) })
    await this.saveAdditionalInfo()
  }

  @catchError async saveContactDetail () {
    await api.postMarkerContactDetail(this.id, this.contact_detail)
  }

  @catchError async saveAdditionalInfo (id) {
    const data = this.additional_info.find(item => item.info_item === id)
    await api.postMarkerAdditionalInfo(this.id, data)
  }

  @action sortAdditionalInfoItem (event) {
    const sorted_items = move(this.additional_info, event)
    this.additional_info = sorted_items
  }

  @catchError async updateStatus (shapeStatus) {
    const response = await api.patchMarkerStatus(this.id, { shape_status: shapeStatus.id })
    runInAction(() => {
      this.status = response.data.shape_status
    })
  }

  @catchError
  async updateTags (tagId) {
    runInAction(() => {
      if (!this.tag_ids.includes(tagId)) this.tag_ids.push(tagId)
    })
    await api.postMarkerTags(this.id, { tag_ids: [tagId] })
  }

  @catchError
  async deleteTag (tagId) {
    runInAction(() => {
      this.tag_ids = this.tag_ids.filter(id => tagId !== id)
    })
    await api.deleteMarkerTag(this.id, tagId)
  }
}

export default SavedMarker
